import React, { Children, Fragment } from 'react';
import PropTypes from 'prop-types';
import BootstrapButton from 'react-bootstrap/Button';
import BootstrapButtonGroup from 'react-bootstrap/ButtonGroup'

export const BUTTON_TYPE = {
  PRIMARY: 'dark',
  SECONDARY: 'light',
  SELECTED: 'light',
  SELECT: 'select',
};

export const BUTTON_POSITION = {
  LEFT: 'left',
  RIGHT: 'right',
  FLOAT: 'float',
  CENTER: 'center',
  NONE: 'none'
};

export const BUTTON_SIZE = {
  AUTO: 'auto',
  FULL: 'full',
  VW10: '10vw',
  VW20: '20vw',
  VW30: '30vw',
  VW40: '40vw',
  VW50: '50vw',
  VW60: '60vw',
  VW70: '70vw',
  VW80: '80vw',
  VW90: '90vw',
  VW100: '100vw'
};

export const TOGGLE_TYPE = {
  RADIO: 'radio',
  CHECKBOX: 'checkbox'
};

export const LINK_TARGET = {
  BLANK: '_blank',
  SELF: '_self',
  PARENT: '_parent',
  TOP: '_top',
};

const Button = ({ variant = BUTTON_TYPE.PRIMARY, position = BUTTON_POSITION.NONE, id, className = '', size = BUTTON_SIZE.FULL, onClick, disabled, children }) => {
  return (
    <>
      {(position === BUTTON_POSITION.CENTER) && (
        <div className='bp-col'>
          <BootstrapButton id={id} variant={variant} className={`${className}`} onClick={onClick} disabled={disabled}>{children}</BootstrapButton>
        </div>
      )}
      {(position === BUTTON_POSITION.LEFT || position === BUTTON_POSITION.RIGHT || position === BUTTON_POSITION.FLOAT) && (
        <div className='bp-col'>
          <BootstrapButton id={id} variant={variant} className={`btn-${size} ${className}`} onClick={onClick} disabled={disabled}>{children}</BootstrapButton>
        </div>
      )}
      {(position !== BUTTON_POSITION.LEFT && position !== BUTTON_POSITION.RIGHT && position !== BUTTON_POSITION.FLOAT && position !== BUTTON_POSITION.CENTER) && (
        <BootstrapButton variant={variant} className={`btn-${size} ${className}`} onClick={onClick} disabled={disabled}>{children}</BootstrapButton>
      )}
    </>
  );
};

Button.propTypes = {
  id: PropTypes.string,
  variant: PropTypes.oneOf(Object.values(BUTTON_TYPE)),
  position: PropTypes.oneOf(Object.values(BUTTON_POSITION)),
  size: PropTypes.oneOf(Object.values(BUTTON_SIZE)),
  className: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool
};

const Group = ({ children, className = '' }) => {
  return (
    <BootstrapButtonGroup className={className}>
      {children}
    </BootstrapButtonGroup>
  );
};

Button.Group = Group;

const Panel = ({ children, className = '', gap = 0 }) => {
  const childList = Children.toArray(children);
  let leftButtons = [];
  let floatButtons = [];
  let rightButtons = [];
  let idx = 0;

  if (childList) {
    Children.map(childList, (child) => {
      if (child.props.position === BUTTON_POSITION.LEFT) {
        leftButtons.push(<Fragment key={idx++}>{child}</Fragment>);
      } else if (child.props.position === BUTTON_POSITION.RIGHT) {
        rightButtons.push(<Fragment key={idx++}>{child}</Fragment>);
      } else {
        floatButtons.push(<Fragment key={idx++}>{child}</Fragment>);
      }
    });
  }

  if (leftButtons.length === 1 && rightButtons.length <= 0 && floatButtons.length <= 0) {
    floatButtons.push(<div className='bp-col' key={idx++}></div>);
  }

  if (leftButtons.length <= 0 && rightButtons.length === 1 && floatButtons.length <= 0) {
    floatButtons.unshift(<div className='bp-col' key={idx++}></div>);
  }

  return (
    <div className={`button-panel ${className}`}>
      <div className='bp-row' style={{gap}}>
        {leftButtons}
        {floatButtons}
        {rightButtons}
      </div>
    </div>
  );
};

Button.Panel = Panel;


class Toggle extends React.Component {
  constructor(props) {
    super(props);
    this.state = { value: props.value, checked: props.checked };
  }

  toggleChange = (e) => {
    const value = e.target.value;
    const checked = e.target.checked;
    this.setState((prevState) => ({ value: this.props.type === TOGGLE_TYPE.CHECKBOX ? [ ...value ] : value, checked: !prevState.checked }));
    this.props.onChange && this.props.onChange(value, checked);
  }

  componentDidUpdate(prevProps) {
    if (this.props.checked !== this.state.checked) {
      this.setState((prevState) => ({ ...prevState, checked: this.props.checked }));
    }
  }

  render() {
    return (
      <label className={`toggle small ${this.props.className || ''}`}>
        {this.props.children}
        <input id={this.props.id} type={this.props.type} name={this.props.name} checked={this.state.checked} value={this.state.value} onChange={this.toggleChange}/>
      </label>
    );
  }

};

Button.Toggle = Toggle;

const ToggleGroup = ({ list = [], type = TOGGLE_TYPE.RADIO, className = '', onChange, children }) => {
  const handleChange = (value, isChecked) => {
    let checkbox = list.find((checkbox) => checkbox.value === value);
    if (checkbox) {
      checkbox.checked = isChecked;
    }
    let _values = list.filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.value);
    typeof onChange === 'function' && onChange(_values);
  };

  return (
    <div className="toggle-list">
      {list.length > 0 && list.map(({title, value, checked}) => (
        <Button.Toggle id={list.id} key={value} type={type} className={className} title={title} value={value} onChange={handleChange} checked={checked} />
      ))}
      {!list.length && children}
    </div>
  );
};

ToggleGroup.propTypes = {
  name: PropTypes.string,
  type: PropTypes.oneOf(Object.values(TOGGLE_TYPE)),
  className: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func
};

Button.ToggleGroup = ToggleGroup;

const Link = ({ variant = BUTTON_TYPE.PRIMARY, position = BUTTON_POSITION.NONE, id, className = '', size = BUTTON_SIZE.FULL, href, target, disabled, children }) => {
  return (
    <>
      {(position === BUTTON_POSITION.CENTER) && (
        <div className='bp-col'>
          <a id={id} target={target} className={`btn btn-link-${variant} ${className}`} href={href} disabled={disabled}>{children}</a>
        </div>
      )}
      {(position === BUTTON_POSITION.LEFT || position === BUTTON_POSITION.RIGHT || position === BUTTON_POSITION.FLOAT) && (
        <div className='bp-col'>
          <a id={id} target={target} className={`btn btn-link-${variant} btn-${size} ${className}`} href={href} disabled={disabled}>{children}</a>
        </div>
      )}
      {(position !== BUTTON_POSITION.LEFT && position !== BUTTON_POSITION.RIGHT && position !== BUTTON_POSITION.FLOAT && position !== BUTTON_POSITION.CENTER) && (
        <a id={id} target={target} className={`btn btn-link-${variant} btn-${size} ${className}`} href={href} disabled={disabled}>{children}</a>
      )}
    </>
  );
};

Link.propTypes = {
  id: PropTypes.string,
  variant: PropTypes.oneOf(Object.values(BUTTON_TYPE)),
  position: PropTypes.oneOf(Object.values(BUTTON_POSITION)),
  size: PropTypes.oneOf(Object.values(BUTTON_SIZE)),
  className: PropTypes.string,
  target: PropTypes.oneOf(Object.values(LINK_TARGET)),
  href: PropTypes.string,
  disabled: PropTypes.bool
};

Button.Link = Link;

export default Button;